import React, { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Color, CommonProperties, CommonStyles } from '../../../styles';
import MapView from 'react-native-maps';
import BottomButton from '../../BottomButton';
import BaseText from '../../BaseText';
import { useNavigation, useRoute } from '@react-navigation/native';
import RowView from '../../RowView';
import moment from 'moment';
import { getNearestAirportFromCoords } from '../../../services/api/search.service';
import * as Location from 'expo-location';
import UserMarker from '../../maps/UserMarker';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserProfileImage } from '../../../store/selectors/profile.selector';
import Images from '../../../constants/Images';
import { setDutyOfCareApi } from '../../../services/aws/trip.service';
import { openAlert } from '../../../store/actions/alert';
import { getLastDutyOfCareByTripKey } from '../../../store/actions/mytrips';
import { useTranslation } from 'react-i18next';
import { i18nNS, useComponentTKey } from '../../../i18n';
import WrapBottomColor from '../../containers/WrapBottomColor';

const getFixedInfo = (position) => {
  if (typeof position === 'number') {
    return position.toFixed(4);
  } else {
    return 0;
  }
};

const TravelerSafetyModal = () => {
  const { t } = useTranslation(i18nNS.tripHub);
  const { tKey } = useComponentTKey('AlertSection');
  const navigation = useNavigation();
  const route = useRoute();
  const { tripKey } = route?.params || {};
  const dispatch = useDispatch();
  const [checked, setChecked] = useState(false);
  const [info, setInfo] = useState({ datetime: null, position: null });
  const [currentLocation, setCurrentLocation] = useState({ city: '', position: null });
  const [isLoading, setIsLoading] = useState(false);
  const userImage = useSelector(selectUserProfileImage);
  const location = currentLocation.position || { latitude: 32.89748, longitude: -97.040443 };
  const region = {
    ...location,
    latitudeDelta: 0.0922,
    longitudeDelta: 0.0421,
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (checked) {
      navigation.goBack();
    } else {
      const current = await getMyPosition();
      const res = await setDutyOfCareApi({
        Latitude: current.position?.latitude,
        Longitude: current.position?.longitude,
        TripKey: tripKey,
      });
      dispatch(getLastDutyOfCareByTripKey(tripKey));
      if (res.Message === 'Success') {
        setChecked(true);
        setInfo({
          datetime: Date.now(),
          position: {
            latitude: 0,
            longitude: 0,
          },
        });
      } else {
        dispatch(
          openAlert({
            title: 'Save your location failed',
            confirmText: 'Ok',
          })
        );
      }
    }
  };

  const getMyPosition = async () => {
    let res = {};
    try {
      setIsLoading(true);
      const obj = await Location.requestForegroundPermissionsAsync();
      if (obj?.status === 'granted') {
        const _location = await Location.getCurrentPositionAsync({});
        const latitude = _location.coords.latitude;
        const longitude = _location.coords.longitude;
        const locationRequest = {
          Latitude: latitude,
          Longitude: longitude,
        };
        const airport = await getNearestAirportFromCoords(locationRequest);

        setIsLoading(false);

        res = {
          city: airport.Code,
          position: {
            latitude,
            longitude,
          },
        };
        setCurrentLocation(res);
      }
    } catch (ex) {
      setIsLoading(false);
      __DEV__ && console.info('get location failed', ex);
    }

    return res;
  };

  useEffect(() => {
    getMyPosition();
  }, []);

  return (
    <WrapBottomColor>
      <View style={CommonStyles.flex1}>
        <View style={[styles.header, checked ? styles.headerChecked : styles.headerNoChecked]}>
          {checked ? (
            <View style={styles.confirmation}>
              <BaseText style={styles.confirmationText}>
                {t(tKey('CheckedIn'))}: {moment(info.datetime).format('D/MM/YYYY')} @$
                {moment(info.datetime).format('h:mm a')}
              </BaseText>
            </View>
          ) : null}
          <View style={styles.regionWrap}>
            <BaseText style={styles.region}>
              {currentLocation.city ? currentLocation.city : ''}
            </BaseText>
            {isLoading ? (
              <BaseText>{t(tKey('LoadingYourCurrentLocation'))}</BaseText>
            ) : (
              <BaseText style={styles.point}>
                {getFixedInfo(location?.latitude)}º N, {getFixedInfo(location?.longitude)}º W
              </BaseText>
            )}
          </View>
          {checked ? (
            <View style={styles.content}>
              <BaseText style={CommonStyles.h1}>{t('DutyofCareCheckedIn.Thankyou')}</BaseText>
              <BaseText style={styles.text}>
                {t('DutyofCareCheckedIn.Remembertoceckinregularlyaswewill')}
              </BaseText>
              <RowView style={CommonStyles.mt5}>
                <BaseText style={CommonStyles.h3}>
                  {t('DutyofCareCheckedIn.StaysafeandgoWINyourtrip')}
                </BaseText>
                <BaseText style={styles.highText}>{t('DutyofCareCheckedIn.WIN')}</BaseText>
                <BaseText style={CommonStyles.h3}>{t('DutyofCareCheckedIn.yourtrip')}</BaseText>
              </RowView>
            </View>
          ) : null}
        </View>
        <MapView style={styles.mapStyle} initialRegion={region} region={region}>
          <UserMarker
            image={userImage ? { uri: userImage } : Images.defaultUser}
            latitude={location.latitude}
            longitude={location.longitude}
          />
        </MapView>
      </View>
      <BottomButton
        full
        label={checked ? t('DutyofCareCheckedIn.ReturntoTriphub') : t(tKey('CheckMyLocation'))}
        onPress={onSubmit}
      />
    </WrapBottomColor>
  );
};

const styles = StyleSheet.create({
  mapStyle: {
    flex: 1,
    ...StyleSheet.absoluteFillObject,
  },
  header: {
    backgroundColor: 'rgba(255,255,255,0.8)',
    zIndex: 2,
  },
  headerChecked: {
    ...StyleSheet.absoluteFillObject,
  },
  headerNoChecked: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
  },
  confirmation: {
    backgroundColor: Color.primary,
    ...CommonProperties.p3,
  },
  confirmationText: {
    textAlign: 'center',
    color: Color.accent,
    ...CommonProperties.h3,
  },
  regionWrap: {
    ...CommonProperties.p3,
  },
  region: {
    ...CommonProperties.h2,
  },
  point: {
    ...CommonProperties.h2,
    color: Color.text.medium,
  },
  text: {
    ...CommonProperties.mt5,
    ...CommonProperties.h3,
    textAlign: 'center',
  },
  highText: {
    ...CommonProperties.h3,
    fontWeight: 'bold',
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default TravelerSafetyModal;
